import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { isLoaded } from 'react-redux-firebase';
import { useNavigate, useParams } from 'react-router-dom';
import { useCustomerState } from '../../features/Briefing/CustomerState';
import { useCountryMarketConfigs } from '../../hooks/queries/useCountryMarketConfigs';
import { useAuth } from '../../hooks/useAuth';
import { useCountryMarketHelpers } from '../../hooks/useCountryMarketHelpers';
import { useGTM } from '../../hooks/useGTM';
import { formatWindowSizeForGTM } from '../../shared/useWindowSize';
import { CountryMarketUrl, TrackingSchema } from '../../types';
import { useCountryMarketState } from './CountryMarketState';

export const CountryMarketController: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const urlParams = useParams<{ country: string; market: string }>();
  const [{ user, auth, customers, customerId }] = useAuth();
  const [{ cpCustomer }, { setCustomerId }] = useCustomerState();
  const {
    findCountryMarketConfigByURLParams,
    isCountryMarketConfigValidForUser,
    isCountryMarketConfigChanged,
  } = useCountryMarketHelpers();
  const { i18n } = useTranslation();
  const [{ countryMarketConfig }, { resetAll, setCountryMarketConfig }] =
    useCountryMarketState();
  const dispatch = useDispatch();
  const { updateDataLayer } = useGTM();
  const availableCountryMarketConfigs = useCountryMarketConfigs();

  useEffect(() => {
    if (availableCountryMarketConfigs) {
      const urlHasCountryMarketParams =
        urlParams.country &&
        urlParams.country !== 'undefined' &&
        urlParams.market &&
        urlParams.market !== 'undefined';
      const newCountryMarketConfig = findCountryMarketConfigByURLParams(
        urlParams.country || window.location.pathname.split("/")[1], // Fallback for when urlParams can't be read, due to weird bug (AZEELAB-555)
        urlParams.market || window.location.pathname.split("/")[2], // Fallback for when urlParams can't be read, due to weird bug (AZEELAB-555)
      );
      const isUserLoaded = auth.isLoaded && !auth.isEmpty && isLoaded(user);
      const isNoUserLoggedIn = auth.isLoaded && auth.isEmpty;

      // Process the changes only after we know definitely
      // that there is a logged in and loaded user
      // or that there is no logged in user
      if (!isUserLoaded && !isNoUserLoggedIn) return;

      if (isUserLoaded) {
        // If there are multiple customers and none is selected by now...
        const customerArray = Object.values(customers || {});
        if (customerArray.length > 1 && !customerId) {
          const customer = customerArray.find(
            ({ current_customer }) => current_customer,
          );
          if (!customer) {
            navigate(CountryMarketUrl.Empty);
            return;
          }
          dispatch(setCustomerId(customer.erp_id));
        } else if (customerArray.length === 1 && !customerId) {
          dispatch(setCustomerId(customerArray[0].erp_id));
        }

        // If there is no countryMarket in the URL...
        if (!urlHasCountryMarketParams && !newCountryMarketConfig) {
          navigate(CountryMarketUrl.Empty);
          return;
        }

        // If the countryMarket is system-invalid...
        if (!newCountryMarketConfig) {
          dispatch(resetAll());
          navigate(CountryMarketUrl.Invalid);
          return;
        }

        // If the countryMarket is user-invalid...
        if (
          newCountryMarketConfig &&
          !isCountryMarketConfigValidForUser(user, newCountryMarketConfig)
        ) {
          dispatch(resetAll());
          navigate(CountryMarketUrl.Invalid);
          return;
        }
      } else {
        // isNoUserLoggedIn == true
        // If there are countryMarket url params AND countryMarketConfig is from URL are invalid for elab system...
        if (urlHasCountryMarketParams && !newCountryMarketConfig) {
          // Redirect to intro page
          navigate('/');
          return;
        } else if (urlHasCountryMarketParams) {
          dispatch(setCountryMarketConfig(newCountryMarketConfig));
          navigate('/');
          return;
        }
      }

      const userHasMultipleSelectionOptions =
        Object.values(user?.countries_and_markets ?? {}).length > 1;

      const isChanged = isCountryMarketConfigChanged(
        newCountryMarketConfig,
        countryMarketConfig,
      );

      if (newCountryMarketConfig && isChanged) {
        const isMarketChanged =
          newCountryMarketConfig.eLabMarketSlug !==
          countryMarketConfig?.eLabMarketSlug;
        const isCountryChanged =
          newCountryMarketConfig.eLabCountrySlug !==
          countryMarketConfig?.eLabCountrySlug;

        if (userHasMultipleSelectionOptions) {
          if (isMarketChanged || isCountryChanged) {
            newCountryMarketConfig.taxonomyMarketAbbreviaton &&
              newCountryMarketConfig.datalakeCountryAbbreviation &&
              updateDataLayer({
                event: 'Update',
                schema: TrackingSchema.site,
                data: {
                  language: i18n.language,
                  environment: process.env.REACT_APP_ENVIRONMENT || 'local',
                  viewport: formatWindowSizeForGTM([
                    window.innerWidth,
                    window.innerHeight,
                  ]),
                  marketSegment:
                    newCountryMarketConfig.taxonomyMarketAbbreviaton,
                  country: newCountryMarketConfig.datalakeCountryAbbreviation,
                },
              });

            if (isMarketChanged) {
              updateDataLayer({
                event: 'Interaction',
                schema: TrackingSchema.interaction,
                data: {
                  group: 'User',
                  name: 'Switch Market Segment',
                },
              });
            }

            if (isCountryChanged) {
              updateDataLayer({
                event: 'Interaction',
                schema: TrackingSchema.interaction,
                data: {
                  group: 'User',
                  name: 'Switch Country',
                },
              });
            }
          }
        }

        dispatch(setCountryMarketConfig(newCountryMarketConfig));
      }
    }
  }, [
    auth.isEmpty,
    auth.isLoaded,
    availableCountryMarketConfigs,
    countryMarketConfig,
    cpCustomer?.erpId,
    customerId,
    customers,
    dispatch,
    findCountryMarketConfigByURLParams,
    i18n.language,
    isCountryMarketConfigChanged,
    isCountryMarketConfigValidForUser,
    navigate,
    resetAll,
    setCountryMarketConfig,
    setCustomerId,
    updateDataLayer,
    urlParams.country,
    urlParams.market,
    user,
  ]);

  return <>{children}</>;
};
